import { type CSSProperties, useRef, useState } from 'react';
import { CITATION_PILL_SHEEN_DURATION_MS } from '@/constants';
import { useColourTheme } from '@/ui/theme/ColourThemeContext';
import { Tooltip, type PrimeTooltipHandle } from '@repo/atomic-components';
import {
  AI_IMAGE_CITATION_WARNING,
  CITATION_TOOLTIP_SURFACE_CLASS,
  CitationTooltipPanel,
} from '@/ui/features/chat/components/CitationTooltipPanel';

/** Pill style for citations from an AI image description, in the warning banner's colours. */
const AI_IMAGE_PILL_CLASS = 'bg-theme-background-yellow border-theme-alert-yellow border-1';

const HIGHLIGHTED_PILL_CLASS = 'bg-theme-highlight-green ring-theme-dark-green ring-1';

interface CitationButtonProps {
  onClick: () => void;
  number: number;
  /** Full citation extract shown in the tooltip body. */
  citationText?: string;
  /** Source document title shown in the tooltip header. */
  documentName?: string;
  /** Optional 1-based page reference shown next to the source title. */
  pageNumber?: number;
  /** Grey styling for citations of documents that cannot be previewed. */
  muted?: boolean;
  /** Citation from an AI image description; the pill is amber. */
  isAiImageDescription?: boolean;
  /** Marks the source of the citation picked in the draft. */
  highlighted?: boolean;
}

/**
 * Pill geometry, shared by the real citation and its streaming placeholder so the number can
 * fill in when the response completes without shifting the surrounding text.
 */
const usePillClass = (isAiImageDescription = false, highlighted = false) => {
  const { theme } = useColourTheme();
  const borderClass = theme === 'dark' ? 'border-theme-border-grey border-1' : '';
  const pillBgClass = theme === 'dark' ? 'bg-theme-white/50' : 'bg-fletchers-gray-110/50';
  const surfaceClass = highlighted
    ? HIGHLIGHTED_PILL_CLASS
    : isAiImageDescription
      ? AI_IMAGE_PILL_CLASS
      : `${pillBgClass} ${borderClass}`;

  return `${surfaceClass} inline-flex h-4.5 min-w-4.5 -translate-y-[2px] items-center justify-center rounded px-1 align-middle text-xs font-semibold`;
};

/** The pill shows only its number, so the name says which source it opens. */
const getCitationLabel = (number: number, documentName?: string, pageNumber?: number) => {
  const source = documentName ? `Source ${number}: ${documentName}` : `Source ${number}`;
  return typeof pageNumber === 'number' ? `${source}, page ${pageNumber}` : source;
};

/** Sheen phase is snapped to this step so pills rendered together are exactly in sync. */
const SHEEN_PHASE_STEP_MS = 250;

interface CitationPlaceholderPillProps {
  /**
   * Whether a number is still coming. Loading pills have the sheen; unresolved ones in a finished response stay still.
   */
  isLoading?: boolean;
}

/** Empty pill for a citation that has no number yet (still streaming, or the map hasn't caught up). */
export const CitationPlaceholderPill = ({ isLoading = true }: CitationPlaceholderPillProps) => {
  const pillClass = usePillClass();

  // Negative delay against a shared clock, so a new pill joins the running sweep. Computed once.
  const [sheenDelayMs] = useState(() => {
    const elapsed = Math.round(performance.now() / SHEEN_PHASE_STEP_MS) * SHEEN_PHASE_STEP_MS;
    return -(elapsed % CITATION_PILL_SHEEN_DURATION_MS);
  });

  return (
    <span
      data-copy-exclude
      aria-hidden
      className={`${pillClass} ${isLoading ? 'citation-pill-loading' : ''}`}
      style={{ '--citation-pill-sheen-delay': `${sheenDelayMs}ms` } as CSSProperties}
    />
  );
};

export const CitationButton = ({
  onClick,
  number,
  citationText,
  documentName,
  pageNumber,
  muted,
  isAiImageDescription,
  highlighted = false,
}: CitationButtonProps) => {
  const pillClass = usePillClass(isAiImageDescription, highlighted);

  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<PrimeTooltipHandle>(null);

  const hasTooltip = Boolean(citationText?.trim());
  const label = getCitationLabel(number, documentName, pageNumber);

  return (
    <>
      <span
        ref={triggerRef}
        role="button"
        tabIndex={0}
        // The caution the amber carries has to be spelled out for anyone who cannot see it.
        aria-label={isAiImageDescription ? `${label}. Warning: ${AI_IMAGE_CITATION_WARNING}` : label}
        aria-current={highlighted || undefined}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        data-copy-exclude
        className={`${pillClass} hover:bg-fletchers-green-50 transition-colors hover:cursor-pointer ${
          highlighted
            ? 'text-theme-dark-green'
            : muted
              ? 'text-theme-text-grey'
              : 'text-theme-black hover:text-fletchers-green-600'
        }`}
      >
        {number}
      </span>
      {hasTooltip && (
        <Tooltip
          ref={tooltipRef}
          target={triggerRef as React.RefObject<HTMLElement>}
          position="top"
          showDelay={120}
          hideDelay={200}
          autoHide={false}
          className={CITATION_TOOLTIP_SURFACE_CLASS}
          baseZIndex={10000}
          richBody={
            <CitationTooltipPanel
              number={number}
              citationText={citationText ?? ''}
              documentName={documentName}
              pageNumber={pageNumber}
              onOpenSource={onClick}
              isAiImageDescription={isAiImageDescription}
            />
          }
        />
      )}
    </>
  );
};
